/**
 * Validation utilities for return URLs in multi-tenant authentication flow
 * Ensures return URLs are secure and prevent open redirects and XSS attacks
 */

export interface ValidationResult {
    isValid: boolean;
    error?: string;
    subdomain?: string;
}

/**
 * Validates a return URL to ensure it's safe to redirect to after authentication
 * @param returnUrl - The URL to validate
 * @param authDomain - The current login domain hostname
 * @returns Validation result with error message if invalid
 */
export const validateReturnUrl = (returnUrl: string, authDomain: string): ValidationResult => {
    try {
        // Parse the return URL
        const url = new URL(returnUrl);
        const authUrl = new URL(`https://${authDomain}`);

        // Development: Allow localhost
        if (url.hostname === "localhost" || url.hostname === "127.0.0.1") {
            if (process.env.NODE_ENV === "development") {
                return { isValid: true, subdomain: "localhost" };
            }
            return { isValid: false, error: "Localhost only allowed in development" };
        }

        // Check protocol is secure
        if (url.protocol !== "https:" && url.protocol !== "http:") {
            return { isValid: false, error: "Invalid protocol. Must be http or https" };
        }

        // Production should use HTTPS
        if (process.env.NODE_ENV === "production" && url.protocol !== "https:") {
            return { isValid: false, error: "HTTPS required in production" };
        }

        // Extract subdomain from return URL
        const subdomain = extractSubdomain(url.hostname);
        if (!subdomain) {
            return { isValid: false, error: "No subdomain found in return URL" };
        }

        // Ensure subdomain is not the login domain
        if (isAuthDomain(url.hostname)) {
            return { isValid: false, error: "Cannot return to login domain" };
        }

        // Verify main domain matches
        const returnUrlMainDomain = extractMainDomain(url.hostname);
        const authMainDomain = extractMainDomain(authUrl.hostname);

        if (returnUrlMainDomain !== authMainDomain) {
            return {
                isValid: false,
                error: `Domain mismatch. Expected ${authMainDomain}, got ${returnUrlMainDomain}`
            };
        }

        return { isValid: true, subdomain };
    } catch (error) {
        return { isValid: false, error: "Invalid URL format" };
    }
};

/**
 * Extracts the main domain from a hostname
 * @param hostname - The hostname to parse (e.g., "trial.kenbakke.com")
 * @returns The main domain (e.g., "kenbakke.com")
 */
export const extractMainDomain = (hostname: string): string => {
    // Handle localhost
    if (hostname === "localhost" || hostname === "127.0.0.1") {
        return "localhost";
    }

    const parts = hostname.split(".");

    // For domains like "trial.kenbakke.com", return "kenbakke.com"
    // Take the last 2 parts
    if (parts.length >= 2) {
        return parts.slice(-2).join(".");
    }

    return hostname;
};

/**
 * Extracts the subdomain portion from a hostname
 * @param hostname - The hostname to parse (e.g., "trial.kenbakke.com")
 * @returns The subdomain (e.g., "trial") or empty string if no subdomain
 */
export const extractSubdomain = (hostname: string): string => {
    // Handle localhost
    if (hostname === "localhost" || hostname === "127.0.0.1") {
        return "localhost";
    }

    const parts = hostname.split(".");

    // Need at least 3 parts to have a subdomain (subdomain.domain.com)
    if (parts.length < 3) {
        return "";
    }

    // Return everything except the last 2 parts
    return parts.slice(0, -2).join(".");
};

/**
 * Checks if a hostname is the login domain
 * @param hostname - The hostname to check
 * @returns True if the hostname is the login domain
 */
export const isAuthDomain = (hostname: string): boolean => {
    const subdomain = extractSubdomain(hostname);

    // Check if subdomain is exactly "login" or starts with "login."
    return subdomain === "login" || subdomain.split(".").includes("login");
};

/**
 * Sanitizes error messages to prevent XSS
 * @param error - The error message to sanitize
 * @returns Sanitized error message
 */
export const sanitizeErrorMessage = (error: string): string => {
    return error.replace(/[<>'"]/g, "");
};
